import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { Notification, NotificationService } from '../services/NotificationService';
import { useUser } from './UserContext';
import { supabase } from '@/integrations/supabase/client';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  hideNotification: (id: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Intervalo de polling em milissegundos (15 segundos - mais frequente para garantir atualizações)
const POLLING_INTERVAL = 15000;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const channelRef = useRef<any>(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const data = await NotificationService.findAll(false);
      setNotifications(data);
    } catch (error) {
      console.error('Failed to fetch notifications', error);
    }
  }, [user]);

  const refreshNotifications = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      await fetchNotifications();
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchNotifications]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      // Limpar intervalos e subscriptions
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    // Buscar notificações imediatamente
    setIsLoading(true);
    fetchNotifications().finally(() => setIsLoading(false));

    // Configurar subscription realtime
    const channelName = `notifications:${user.id}`;
    const channel = supabase
      .channel(channelName, {
        config: {
          broadcast: { self: true },
        },
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('🔔 Notification change received!', payload);
          if (payload.eventType === 'INSERT') {
            setNotifications(prev => {
              // Evitar duplicatas
              const exists = prev.some(n => n.id === payload.new.id);
              if (exists) return prev;
              return [payload.new as Notification, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            setNotifications(prev => prev.map(n => n.id === payload.new.id ? payload.new as Notification : n));
          } else if (payload.eventType === 'DELETE') {
            setNotifications(prev => prev.filter(n => n.id !== payload.old.id));
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.warn('⚠️ Realtime subscription error, falling back to polling');
        }
      });

    channelRef.current = channel;

    // Configurar polling como fallback (busca periódica)
    pollingIntervalRef.current = setInterval(() => {
      console.log('🔄 Polling notifications...');
      fetchNotifications();
    }, POLLING_INTERVAL);

    return () => {
      // Limpar subscription
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      // Limpar polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [user, fetchNotifications]);

  const markAsRead = async (id: string) => {
    try {
      await NotificationService.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      console.error('Failed to mark as read', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Excluir notificações de workflow pendentes (excludeWorkflowPending = true)
      await NotificationService.markAllAsRead(true);
      // Atualizar apenas as notificações que não são de workflow pendentes
      setNotifications(prev => prev.map(n => {
        // Se é notificação de workflow (tem approvalId no metadata), manter como não lida
        if (n.metadata?.approvalId) {
          return n;
        }
        return { ...n, read: true };
      }));
    } catch (error) {
      console.error('Failed to mark all as read', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await NotificationService.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Failed to delete notification', error);
    }
  };

  const hideNotification = async (id: string) => {
    try {
      await NotificationService.hide(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Failed to hide notification', error);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      await NotificationService.deleteAll();
      setNotifications([]);
    } catch (error) {
      console.error('Failed to delete all notifications', error);
    }
  };

  return (
    <NotificationContext.Provider value={{ 
      notifications, 
      unreadCount, 
      markAsRead, 
      markAllAsRead, 
      deleteNotification,
      deleteAllNotifications,
      hideNotification,
      refreshNotifications, 
      isLoading 
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
